import { useMemo } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import { useLandingContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LandingContext';
import { EventLink } from 'hb-react/frontoffice/components/EventLink';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import EventCanceledLabel from 'hb-react/shared/apps/Events/components/EventCanceledLabel';
import useEventDisplayTimeZone from 'hb-react/shared/components/EventDate/hooks/useEventDisplayTimeZone';
import getDateStr from 'hb-react/shared/components/EventDate/utils/getDateStr';

import Landing from '../Landing';
import { EventAttendees, Wrapper, WrapperEventImage } from '../UserEvent';

const NetworkEvent = () => {
  const {
    cancelled,
    coverPicture,
    id,
    location,
    logo,
    startDate,
    timezone,
    title,
    type,
  } = useLandingContext();
  const { currentNetwork } = useDefaultAppContexts();
  const isMobileDevice = useIsMobileDevice();

  const eventDisplayTimeZone = useEventDisplayTimeZone(timezone);

  const commentsEnabled =
    currentNetwork?.network_configuration?.events?.comments_enabled;

  const dateStr = useMemo(
    () =>
      getDateStr({
        startDate,
        endDate: startDate,
        timezone: eventDisplayTimeZone,
      }),
    [startDate, eventDisplayTimeZone],
  );

  const actualImage =
    !logo ||
    logo.preview_url.includes('/missing/') ||
    logo.preview_url === currentNetwork?.avatar_thumb_url
      ? coverPicture.preview_url
      : logo.preview_url;

  return (
    <Landing
      title={s__('LiveFeed|created an event <strong>%{title}</strong>').i__({
        title,
      })}
      commentsEnabled={commentsEnabled}
    >
      <Wrapper>
        <Box data-testid={`feed-event-${id}`} flexDirection="column">
          <Box position="relative">
            <Box position="absolute" top={2} left={2}>
              <EventCanceledLabel event={{ cancelled }} />
            </Box>

            <WrapperEventImage
              src={actualImage}
              opacity={cancelled ? 0.4 : 1}
              data-testid="event-image"
            />
            <Box
              flex={1}
              flexDirection="column"
              justifyContent="space-between"
              maxWidth={{
                _: 'calc(100% - 102px)',
                m: 'calc(100% - 200px)',
              }}
              pl={{ _: 4, m: 6 }}
              pr={{ _: 0, m: 4 }}
              py={{ _: 0, m: 3 }}
            >
              <Box font="h4-semibold" title={title}>
                <Dotdotdot clamp={1}>{title}</Dotdotdot>
              </Box>
              <Box mt={1}>
                <Dotdotdot clamp={1}>{dateStr}</Dotdotdot>
              </Box>
              <Dotdotdot clamp={1}>{location?.address}</Dotdotdot>
              <Box display="block" mt={3}>
                <EventLink event={{ id, type, title }} as="Button" mt={0} />
              </Box>
              {!isMobileDevice && (
                <Box flexDirection="column">
                  <EventAttendees eventId={id} />
                </Box>
              )}
            </Box>
          </Box>
          {isMobileDevice && (
            <Box
              flex={1}
              flexDirection="column"
              pl={{ _: 0, m: 6 }}
              pr={{ _: 0, m: 4 }}
              py={{ _: 0, m: 3 }}
            >
              <EventAttendees eventId={id} />
            </Box>
          )}
        </Box>
      </Wrapper>
    </Landing>
  );
};

export default NetworkEvent;
